import { create } from 'zustand';
import { v4 as uuidv4 } from 'uuid';
import type {
  Project,
  Application,
  VMConfig,
  SizingConfig,
  SizingScenario,
  Tier,
} from '../types';

// ─── Default Sizing Config ────────────────────────────────────────────────────

const DEFAULT_SIZING_CONFIG: SizingConfig = {
  vcpuOvercommitRatio: 4,
  memoryOvercommitRatio: 1,
  platformOverheadPct: 0.15,
  haModel: 'n_plus_1',
  redundancyModel: 'active_passive',
  drTier: 'warm',
  drComputeFractionPct: 0.5,
  storageArchitecture: 'traditional_san',
  storageUsableToRawRatio: 1.33,
  storageGrowthBufferPct: 0.2,
  raidWritePenaltyFactor: 4,
  perHostTdpWatts: 500,
  storageChangeRatePct: 0.05,
  drRpoHours: 4,
};

// ─── Store Shape ──────────────────────────────────────────────────────────────

interface ProjectState {
  project: Project | null;

  // Project
  setProject: (project: Project) => void;
  clearProject: () => void;

  // Applications
  addApplication: (name: string, description?: string) => void;
  updateApplication: (id: string, partial: Partial<Pick<Application, 'name' | 'description'>>) => void;
  deleteApplication: (id: string) => void;

  // Tiers
  addTier: (applicationId: string, name: string, description?: string) => void;
  updateTier: (id: string, partial: Partial<Pick<Tier, 'name' | 'description' | 'sortOrder'>>) => void;
  deleteTier: (id: string) => void;
  reorderTiers: (applicationId: string, tierIds: string[]) => void;

  // VMs
  addVM: (applicationId: string, tierId: string, vm: Omit<VMConfig, 'id' | 'applicationId' | 'tierId'>) => void;
  updateVM: (id: string, partial: Partial<Omit<VMConfig, 'id' | 'applicationId'>>) => void;
  deleteVM: (id: string) => void;

  // Scenarios
  addScenario: (name: string, baseConfig?: SizingConfig) => void;
  updateScenario: (id: string, partial: Partial<Pick<SizingScenario, 'name' | 'description' | 'sizingConfig'>>) => void;
  deleteScenario: (id: string) => void;
  setActiveScenario: (id: string) => void;

  // Sizing config (updates the active scenario's config)
  updateSizingConfig: (partial: Partial<SizingConfig>) => void;

  // Share links
  setShareCode: (code: string) => void;
}

// ─── Store Implementation ─────────────────────────────────────────────────────

export const useProjectStore = create<ProjectState>((set, get) => ({
  project: null,

  setProject: (project) => set({ project }),

  clearProject: () => set({ project: null }),

  // ── Applications ────────────────────────────────────────────────────────────

  addApplication: (name, description) => {
    const { project } = get();
    if (!project) return;
    const appId = uuidv4();
    const defaultTier: Tier = {
      id: uuidv4(),
      applicationId: appId,
      name: 'Default',
      sortOrder: 0,
    };
    const newApp: Application = {
      id: appId,
      projectId: project.id,
      name,
      description,
      tiers: [defaultTier],
      vms: [],
    };
    set({
      project: {
        ...project,
        applications: [...project.applications, newApp],
        updatedAt: new Date().toISOString(),
      },
    });
  },

  updateApplication: (id, partial) => {
    const { project } = get();
    if (!project) return;
    set({
      project: {
        ...project,
        applications: project.applications.map(app =>
          app.id === id ? { ...app, ...partial } : app,
        ),
        updatedAt: new Date().toISOString(),
      },
    });
  },

  deleteApplication: (id) => {
    const { project } = get();
    if (!project) return;
    set({
      project: {
        ...project,
        applications: project.applications.filter(app => app.id !== id),
        updatedAt: new Date().toISOString(),
      },
    });
  },

  // ── Tiers ───────────────────────────────────────────────────────────────────

  addTier: (applicationId, name, description) => {
    const { project } = get();
    if (!project) return;
    const app = project.applications.find(a => a.id === applicationId);
    if (!app) return;
    const newTier: Tier = {
      id: uuidv4(),
      applicationId,
      name,
      description,
      sortOrder: app.tiers.length,
    };
    set({
      project: {
        ...project,
        applications: project.applications.map(a =>
          a.id === applicationId
            ? { ...a, tiers: [...a.tiers, newTier] }
            : a,
        ),
        updatedAt: new Date().toISOString(),
      },
    });
  },

  updateTier: (id, partial) => {
    const { project } = get();
    if (!project) return;
    set({
      project: {
        ...project,
        applications: project.applications.map(app => ({
          ...app,
          tiers: app.tiers.map(tier =>
            tier.id === id ? { ...tier, ...partial } : tier,
          ),
        })),
        updatedAt: new Date().toISOString(),
      },
    });
  },

  deleteTier: (id) => {
    const { project } = get();
    if (!project) return;
    // Find which app owns this tier
    const app = project.applications.find(a => a.tiers.some(t => t.id === id));
    if (!app) return;
    // Prevent deletion if it's the last tier
    if (app.tiers.length <= 1) return;
    const remaining = app.tiers.filter(t => t.id !== id);
    const reassignTo = remaining[0].id;
    set({
      project: {
        ...project,
        applications: project.applications.map(a =>
          a.id === app.id
            ? {
                ...a,
                tiers: remaining,
                vms: a.vms.map(vm =>
                  vm.tierId === id ? { ...vm, tierId: reassignTo } : vm,
                ),
              }
            : a,
        ),
        updatedAt: new Date().toISOString(),
      },
    });
  },

  reorderTiers: (applicationId, tierIds) => {
    const { project } = get();
    if (!project) return;
    set({
      project: {
        ...project,
        applications: project.applications.map(app => {
          if (app.id !== applicationId) return app;
          const reordered = tierIds
            .map((id, index) => {
              const tier = app.tiers.find(t => t.id === id);
              return tier ? { ...tier, sortOrder: index } : null;
            })
            .filter((t): t is Tier => t !== null);
          return { ...app, tiers: reordered };
        }),
        updatedAt: new Date().toISOString(),
      },
    });
  },

  // ── VMs ─────────────────────────────────────────────────────────────────────

  addVM: (applicationId, tierId, vmData) => {
    const { project } = get();
    if (!project) return;
    const newVm: VMConfig = { ...vmData, id: uuidv4(), applicationId, tierId };
    set({
      project: {
        ...project,
        applications: project.applications.map(app =>
          app.id === applicationId
            ? { ...app, vms: [...app.vms, newVm] }
            : app,
        ),
        updatedAt: new Date().toISOString(),
      },
    });
  },

  updateVM: (id, partial) => {
    const { project } = get();
    if (!project) return;
    set({
      project: {
        ...project,
        applications: project.applications.map(app => ({
          ...app,
          vms: app.vms.map(vm =>
            vm.id === id ? { ...vm, ...partial } : vm,
          ),
        })),
        updatedAt: new Date().toISOString(),
      },
    });
  },

  deleteVM: (id) => {
    const { project } = get();
    if (!project) return;
    set({
      project: {
        ...project,
        applications: project.applications.map(app => ({
          ...app,
          vms: app.vms.filter(vm => vm.id !== id),
        })),
        updatedAt: new Date().toISOString(),
      },
    });
  },

  // ── Scenarios ───────────────────────────────────────────────────────────────

  addScenario: (name, baseConfig) => {
    const { project } = get();
    if (!project) return;
    const activeScenario = project.scenarios.find(s => s.id === project.activeScenarioId);
    const newScenario: SizingScenario = {
      id: uuidv4(),
      name,
      sizingConfig: baseConfig ?? activeScenario?.sizingConfig ?? DEFAULT_SIZING_CONFIG,
      createdAt: new Date().toISOString(),
      isBaseline: false,
    };
    set({
      project: {
        ...project,
        scenarios: [...project.scenarios, newScenario],
        activeScenarioId: newScenario.id,
        updatedAt: new Date().toISOString(),
      },
    });
  },

  updateScenario: (id, partial) => {
    const { project } = get();
    if (!project) return;
    set({
      project: {
        ...project,
        scenarios: project.scenarios.map(s =>
          s.id === id ? { ...s, ...partial } : s,
        ),
        updatedAt: new Date().toISOString(),
      },
    });
  },

  deleteScenario: (id) => {
    const { project } = get();
    if (!project) return;
    // Cannot delete the baseline scenario or the last scenario
    const scenario = project.scenarios.find(s => s.id === id);
    if (!scenario || scenario.isBaseline || project.scenarios.length <= 1) return;
    const remaining = project.scenarios.filter(s => s.id !== id);
    const nextActive = project.activeScenarioId === id
      ? (remaining.find(s => s.isBaseline) ?? remaining[0]).id
      : project.activeScenarioId;
    set({
      project: {
        ...project,
        scenarios: remaining,
        activeScenarioId: nextActive,
        updatedAt: new Date().toISOString(),
      },
    });
  },

  setActiveScenario: (id) => {
    const { project } = get();
    if (!project) return;
    if (!project.scenarios.find(s => s.id === id)) return;
    set({
      project: {
        ...project,
        activeScenarioId: id,
        updatedAt: new Date().toISOString(),
      },
    });
  },

  // ── Share Links ──────────────────────────────────────────────────────────────

  setShareCode: (code: string) => {
    const { project } = get();
    if (!project) return;
    set({ project: { ...project, shareCode: code, updatedAt: new Date().toISOString() } });
  },

  // ── Sizing Config ────────────────────────────────────────────────────────────

  updateSizingConfig: (partial: Partial<SizingConfig>) => {
    const { project } = get();
    if (!project) return;
    set({
      project: {
        ...project,
        scenarios: project.scenarios.map((s: SizingScenario) =>
          s.id === project.activeScenarioId
            ? { ...s, sizingConfig: { ...s.sizingConfig, ...partial } }
            : s,
        ),
        updatedAt: new Date().toISOString(),
      },
    });
  },
}));
